/**
 * Mensajes de notificación centralizados en español.
 * Voz del producto: docs/voice.md (usted + impersonal, éxitos "con éxito", sin -mente).
 */

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info';

export interface NotificationMessage {
  message: string;
  severity: NotificationSeverity;
}

// ============================================
// MENSAJES DE PRODUCTOS
// ============================================
export const PRODUCT_MESSAGES = {
  CREATED_SUCCESS: {
    message: 'Producto creado con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  UPDATED_SUCCESS: {
    message: 'Producto actualizado con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  SAVE_ERROR: {
    message: 'No se pudo guardar el producto. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  EXPORTING: {
    message: 'Exportando productos...',
    severity: 'info' as NotificationSeverity,
  },
  EXPORTED_SUCCESS: {
    message: 'Productos exportados con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  EXPORT_ERROR: {
    message: 'No se pudo exportar los productos. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  IMPORT_NOT_IMPLEMENTED: {
    message: 'La importación aún no está disponible.',
    severity: 'info' as NotificationSeverity,
  },
} as const;

// ============================================
// MENSAJES DE INVENTARIO
// ============================================
export const INVENTORY_MESSAGES = {
  RECORD_CREATED_WITH_ITEMS: (itemsCount: number) => ({
    message: `Registro creado con ${itemsCount} productos y piezas.`,
    severity: 'success' as NotificationSeverity,
  }),
  RECORD_CREATED: (itemsCount: number) => ({
    message: `Registro creado con ${itemsCount} productos.`,
    severity: 'success' as NotificationSeverity,
  }),
  RECORD_UPDATED_WITH_ITEMS: (itemsCount: number) => ({
    message: `Registro actualizado con ${itemsCount} productos y piezas.`,
    severity: 'success' as NotificationSeverity,
  }),
  RECORD_UPDATED: (itemsCount: number) => ({
    message: `Registro actualizado con ${itemsCount} productos.`,
    severity: 'success' as NotificationSeverity,
  }),
  RECORD_CANCELED_SUCCESS: {
    message: 'Registro cancelado con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  CREATE_ERROR: {
    message: 'No se pudo crear el registro de inventario. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  UPDATE_ERROR: {
    message: 'No se pudo actualizar el registro de inventario. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  LOAD_BRANCHES_ERROR: {
    message: 'No se pudieron cargar las sucursales. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  LOAD_DETAILS_ERROR: {
    message: 'No se pudieron cargar los detalles del registro. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  LOAD_DETAILS_EDIT_ERROR: {
    message: 'No se pudieron cargar los detalles para editar. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  LOAD_DETAILS_PRINT_ERROR: {
    message: 'No se pudieron cargar los detalles para imprimir. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  CANNOT_EDIT_COMPLETED: {
    message: 'No se puede editar un registro completado.',
    severity: 'warning' as NotificationSeverity,
  },
  IMPORT_NOT_IMPLEMENTED: {
    message: 'La importación aún no está disponible.',
    severity: 'info' as NotificationSeverity,
  },
  EXPORT_NOT_IMPLEMENTED: {
    message: 'La exportación aún no está disponible.',
    severity: 'info' as NotificationSeverity,
  },
  EXPORTING: {
    message: 'Exportando registros de inventario...',
    severity: 'info' as NotificationSeverity,
  },
  EXPORTED_SUCCESS: {
    message: 'Registros de inventario exportados con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  EXPORT_ERROR: (errorMessage?: string) => ({
    message: errorMessage ?? 'No se pudo exportar los registros de inventario. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  }),
  PRODUCT_RESERVED_SUCCESS: {
    message: 'Pieza bloqueada con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  RESERVE_ERROR: (errorMessage?: string) => ({
    message: errorMessage ?? 'No se pudo bloquear la pieza. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  }),
  RESERVE_RELEASED_SUCCESS: {
    message: 'Bloqueo liberado con éxito. La pieza está disponible de nuevo.',
    severity: 'success' as NotificationSeverity,
  },
  RELEASE_RESERVE_ERROR: (errorMessage?: string) => ({
    message: errorMessage ?? 'No se pudo liberar el bloqueo de la pieza. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  }),
} as const;

// ============================================
// MENSAJES DE USUARIOS
// ============================================
export const USER_MESSAGES = {
  DUPLICATE_EMAIL: {
    message: 'Este correo electrónico ya está registrado.',
    severity: 'error' as NotificationSeverity,
  },
  CREATE_ERROR: {
    message: 'No se pudo crear el usuario. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  UPDATE_ERROR: {
    message: 'No se pudo actualizar el usuario. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
} as const;

// ============================================
// MENSAJES DE TRANSFERENCIAS DE INVENTARIO
// ============================================
export const TRANSFER_MESSAGES = {
  ACCEPTED_SUCCESS: {
    message: 'Transferencia aceptada con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  REJECTED_SUCCESS: {
    message: 'Transferencia rechazada con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  CONFIRM_ERROR: (errorMessage?: string) => ({
    message: errorMessage ?? 'No se pudo confirmar la transferencia. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  }),
  EXPORTING: {
    message: 'Exportando transferencias...',
    severity: 'info' as NotificationSeverity,
  },
  EXPORTED_SUCCESS: {
    message: 'Transferencias exportadas con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  EXPORT_ERROR: (errorMessage?: string) => ({
    message: errorMessage ?? 'No se pudo exportar las transferencias. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  }),
} as const;

// ============================================
// MENSAJES DE ÓRDENES DE VENTA
// ============================================
export const SALES_ORDER_MESSAGES = {
  LOAD_BRANCHES_ERROR: {
    message: 'No se pudieron cargar las sucursales. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  EXPORT_NOT_IMPLEMENTED: {
    message: 'La exportación aún no está disponible.',
    severity: 'info' as NotificationSeverity,
  },
  EDIT_NOT_IMPLEMENTED: {
    message: 'La edición aún no está disponible.',
    severity: 'info' as NotificationSeverity,
  },
  LOAD_ORDER_EDIT_ERROR: {
    message: 'No se pudo cargar la venta para editar. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  DELIVERY_NOT_IMPLEMENTED: {
    message: 'La entrega desde esta acción aún no está disponible.',
    severity: 'info' as NotificationSeverity,
  },
  MARK_DELIVERED_ERROR: {
    message: 'No se pudo marcar la venta como entregada. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  PRINT_NOT_IMPLEMENTED: {
    message: 'La impresión aún no está disponible.',
    severity: 'info' as NotificationSeverity,
  },
  PRINT_ERROR: {
    message: 'No se pudo imprimir el recibo. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  LOAD_ORDER_PAYMENT_ERROR: {
    message: 'No se pudo cargar la venta para el pago. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  OPEN_RETURN_ERROR: (errorMessage: string) => ({
    message: `No se pudo abrir la devolución: ${errorMessage}`,
    severity: 'error' as NotificationSeverity,
  }),
  LOAD_INSTALLMENTS_ERROR: {
    message: 'No se pudo cargar la venta para las cuotas. Intente de nuevo.',
    severity: 'error' as NotificationSeverity,
  },
  PAYMENT_PROCESSED_SUCCESS: {
    message: 'Pago registrado con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  INSTALLMENTS_UPDATED_SUCCESS: {
    message: 'Cuotas actualizadas con éxito.',
    severity: 'success' as NotificationSeverity,
  },
  RETURN_PROCESS_ERROR: (errorMessage: string) => ({
    message: `No se pudo procesar la devolución: ${errorMessage}`,
    severity: 'error' as NotificationSeverity,
  }),
} as const;

// ============================================
// FUNCIÓN HELPER PARA CREAR NOTIFICACIONES CON MENSAJES PERSONALIZADOS
// ============================================
export const createNotification = (
  message: string,
  severity: NotificationSeverity = 'info',
): NotificationMessage => ({
  message,
  severity,
});

// ============================================
// FUNCIÓN HELPER PARA CREAR NOTIFICACIONES DE ERROR CON MENSAJES DEL SERVIDOR
// ============================================
export const createErrorNotification = (
  defaultMessage: string,
  error?: any,
): NotificationMessage => {
  let errorMessage = defaultMessage;

  if (error?.response?.data?.message) {
    if (Array.isArray(error.response.data.message)) {
      errorMessage = `Error: ${error.response.data.message.join(', ')}`;
    } else {
      errorMessage = `Error: ${error.response.data.message}`;
    }
  } else if (error?.message) {
    errorMessage = `Error: ${error.message}`;
  }

  return {
    message: errorMessage,
    severity: 'error',
  };
};
